import AppContext from 'App/AppContext';
import { IPCheckbox, IPFontIcon, IPLabel, IPScrollablePane } from 'baseComponents';
import { IPScrollbarVisibility } from 'baseComponents/ScrollablePane/IPScrollablePane';
import { IPStack } from 'baseComponents/Stack/IPStack';
import AuthSchemaPicker from 'components/Pickers/AuthSchemaPicker';
import MultiSelectUserPicker from 'components/Pickers/MultiSelectUserPicker';
import {
  globalStackItemStylesPaddingSceneScroll,
  globalStackTokensGapExtraSmall,
  globalStackTokensGapMedium,
  globalStackTokensGapSmall,
  userIcon,
} from 'globalStyles';
import Tag from 'models/tag';
import Task, { TaskTypes } from 'models/tasks/task';
import User from 'models/user';
import { Fragment, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { newGuid, newGuidNil } from 'utils/guid';
import { TaskDetailDescription } from '../Details/TaskDetailDecription';
import { TaskDetailName } from '../Details/TaskDetailName';
import { TaskDetailStatusActive } from '../Details/TaskDetailStatusActive';
import { TaskDetailTags } from '../Details/TaskDetailTags';

interface IApprovalTemplateTaskDetail {
  task: Task;
  orgTask: Task;
  getErrorMessage: (field: string) => string | undefined;
  onUpdate: (newTask: Task) => void;
  isActionPending: boolean;
  tags: Tag[];
  addTagToTaskState: (tag: Tag) => void;
  removeTagFromTaskState: (tag: Tag) => void;
  onChangeCompletionDate: (completionDate: Date) => void;
  windowLevel?: number;
  canUpdate: boolean;
}

export const ApprovalTemplateTaskDetail = (props: IApprovalTemplateTaskDetail) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['task', 'control', 'translation']);

  const iconFontSize = { fontSize: '20px', marginTop: '5px' };

  //
  // Workflow steps
  //
  const getUsersFromInstances = (): User[] => {
    const userIds = props.task.instances?.filter((i) => i.userId).map((i) => i.userId as string);
    if (userIds) {
      return appContext.globalDataCache.users.getItemsForId(userIds);
    } else {
      return [];
    }
  };

  const createInstancesForUsers = (users: User[] | undefined) => {
    let newInstances: Task[] = props.task.instances ? [...props.task.instances] : [];

    if (users) {
      for (let idx = 0; idx < users.length; idx++) {
        const user = users[idx];
        if (!newInstances.some((i) => i.userId === user.id)) {
          //create a new instance for this user
          const newSubTask = new Task();
          newSubTask.id = newGuid();
          newSubTask.name = user.name;
          newSubTask.taskType = TaskTypes.EventTemplate;
          newSubTask.taskTypeId = props.task.taskTypeId;
          newSubTask.taskMasterId = props.task.taskId;
          newSubTask.taskStates = appContext.globalDataCache.taskStates.items;
          newSubTask.taskStateId = newSubTask.getFirstState();
          newSubTask.userId = user.id;
          newSubTask.user = user;
          newSubTask.creatorId = user.id;
          newSubTask.creator = user;
          newSubTask.ownerId = user.id;
          newSubTask.owner = user;
          newInstances.push(newSubTask);
        }
      }
    }

    //remove instances for deleted users
    const instancesToRemove: string[] = [];

    if (users) {
      for (let idx = 0; idx < newInstances.length; idx++) {
        const instance = newInstances[idx];
        if (instance.id && instance.userId !== undefined && !users.some((u) => u.id === instance.userId)) {
          instancesToRemove.push(instance.id);
        }
      }
      for (let idx = 0; idx < instancesToRemove.length; idx++) {
        const idxToRemove = newInstances.findIndex((i) => i.id === instancesToRemove[idx]);
        newInstances.splice(idxToRemove, 1);
      }
    } else {
      //remove all instances
      newInstances = [];
    }

    const newTask = props.task.clone();
    newTask.instances = newInstances;
    props.onUpdate(newTask);
  };

  const setInstanceForOwner = (enabled: boolean) => {
    let newInstances: Task[] = props.task.instances ? [...props.task.instances] : [];

    if (enabled && !newInstances.some((i) => i.userId === newGuidNil())) {
      //create a new instance for an owner
      //this is indicated with an unassigned instance: userId = undefined
      const newSubTask = new Task();
      newSubTask.name = 'Owner';
      newSubTask.taskType = TaskTypes.EventTemplate;
      newSubTask.taskTypeId = props.task.taskTypeId;
      newSubTask.taskMasterId = props.task.taskId;
      newSubTask.taskStates = appContext.globalDataCache.taskStates.items;
      newSubTask.taskStateId = newSubTask.getFirstState();
      newSubTask.userId = undefined;
      newSubTask.creatorId = props.task.creatorId;
      newSubTask.creator = props.task.creator;
      newSubTask.ownerId = props.task.ownerId;
      newSubTask.owner = props.task.owner;
      newInstances.push(newSubTask);
    } else if (!enabled) {
      newInstances = newInstances.filter((i) => i.userId !== undefined);
    }

    const newTask = props.task.clone();
    newTask.instances = newInstances;
    props.onUpdate(newTask);
  };

  //
  // Main render
  //
  return (
    <IPStack verticalFill tokens={globalStackTokensGapSmall}>
      <IPStack.Item grow styles={globalStackItemStylesPaddingSceneScroll}>
        <IPScrollablePane scrollbarVisibility={IPScrollbarVisibility.auto}>
          <IPStack verticalFill tokens={globalStackTokensGapSmall} styles={{ root: { paddingRight: 10 } }}>
            <TaskDetailName
              orgTask={props.orgTask}
              task={props.task}
              canUpdate={props.canUpdate}
              onUpdate={props.onUpdate}
              getErrorMessage={props.getErrorMessage}
            />
            <TaskDetailTags
              orgTask={props.orgTask}
              task={props.task}
              tags={props.tags}
              canUpdate={props.canUpdate}
              addTagToTaskState={props.addTagToTaskState}
              removeTagFromTaskState={props.removeTagFromTaskState}
              isActionPending={props.isActionPending}
            />
            <TaskDetailDescription
              orgTask={props.orgTask}
              task={props.task}
              canUpdate={props.canUpdate}
              onUpdate={props.onUpdate}
            />
            <Fragment>
              <IPStack
                horizontal
                tokens={appContext.isMobileView ? globalStackTokensGapExtraSmall : globalStackTokensGapMedium}
                verticalAlign="start"
              >
                <IPStack.Item>
                  {!appContext.isMobileView && <IPFontIcon iconName="FavoriteStarFill" style={iconFontSize} />}
                </IPStack.Item>
                <IPStack.Item>
                  <AuthSchemaPicker
                    selectedAuthSchemaId={props.task.authSchemaId}
                    onSelect={(schema) => {
                      const copyTask = props.task.clone();
                      copyTask.authSchemaId = schema?.authSchemaId;
                      props.onUpdate(copyTask);
                    }}
                  />
                </IPStack.Item>
                <TaskDetailStatusActive
                  orgTask={props.orgTask}
                  task={props.task}
                  canUpdate={props.canUpdate}
                  onUpdate={props.onUpdate}
                  checkOnly={true}
                />
              </IPStack>
              <IPStack.Item>
                <IPStack
                  horizontal
                  tokens={appContext.isMobileView ? globalStackTokensGapExtraSmall : globalStackTokensGapMedium}
                  verticalAlign="start"
                >
                  <IPStack.Item>
                    {!appContext.isMobileView && <IPFontIcon iconName={userIcon.iconName} style={iconFontSize} />}
                  </IPStack.Item>
                  <IPStack.Item grow>
                    <IPStack tokens={globalStackTokensGapSmall}>
                      <IPStack>
                        <IPLabel>{t('task:TaskTypes.ApprovalTemplate.Approvers')}</IPLabel>
                        <MultiSelectUserPicker
                          users={undefined}
                          itemLimit={5}
                          selectedUsers={getUsersFromInstances()}
                          onMultipleSelect={(items: User[] | undefined) => {
                            createInstancesForUsers(items);
                          }}
                        />
                      </IPStack>
                      <IPCheckbox
                        checked={props.task.instances?.some((i) => i.userId === undefined)}
                        label={t('task:TaskTypes.ApprovalTemplate.IncludeOwners')}
                        onChange={(ev, checked) => {
                          setInstanceForOwner(checked ?? false);
                        }}
                      />
                      <IPCheckbox
                        checked={props.task.instancesEnabled}
                        label={t('task:TaskTypes.ApprovalTemplate.UserCanChangeApprovers')}
                        onChange={(ev, checked) => {
                          const copyTask = props.task.clone();
                          copyTask.instancesEnabled = checked;
                          props.onUpdate(copyTask);
                        }}
                      />
                    </IPStack>
                  </IPStack.Item>
                </IPStack>
              </IPStack.Item>
            </Fragment>
          </IPStack>
        </IPScrollablePane>
      </IPStack.Item>
    </IPStack>
  );
};
